'use client';

import { c as _c } from "react/compiler-runtime";
import type { PrV2NavigationOverlayEntrance } from '@/app/pr-v2/[owner]/[repo]/pull/[number]/components/shared/skeletons/pr-v2-navigation-loading-overlay';
import type { PrOpenSource } from '@/lib/pr-v2/pr-open-timing';
import { useRouter } from 'next/navigation';
import React from 'react';
import { PrV2NavigationLoadingOverlay } from '@/app/pr-v2/[owner]/[repo]/pull/[number]/components/shared/skeletons/pr-v2-navigation-loading-overlay';
import { recordPrNavigationStart } from '@/lib/pr-v2/pr-open-timing';
import { parsePrV2Route } from '@/lib/pr-v2/route-utils';
import { usePrV2ViewPreferencesStore } from '@/lib/stores/pr-v2-view-preferences-store';
import { useZenModeStore } from '@/lib/stores/zen-mode-store';
type PrNavigationOptions = {
  /** The surface opening the PR, as the PR page's open timing reports it. */
  source: PrOpenSource;
  /** How the loading overlay appears. Defaults to 'in-window'. */
  entrance?: PrV2NavigationOverlayEntrance;
};
type PrNavigation = {
  /** Href of the navigation to another pull request currently in flight, or null when idle. */
  pendingHref: string | null;
  /** Pushes the route inside a transition so consumers can render pending feedback. */
  navigate: (href: string, options: PrNavigationOptions) => void;
};
type PendingPrNavigation = {
  href: string;
  entrance: PrV2NavigationOverlayEntrance;
};

// Exported for tests that need to drive a specific pending state.
export const PrNavigationContext = React.createContext<PrNavigation | null>(null);
function opensAnotherPullRequest(href: string, currentPathname: string): boolean {
  const target = parsePrV2Route(href.replace(/[?#].*$/, ''));
  if (target === null) {
    return false;
  }
  const current = parsePrV2Route(currentPathname);
  return current === null || current.owner !== target.owner || current.repo !== target.repo || current.number !== target.number;
}

/**
 * Owns every in-app navigation to a pull request. The PR route renders no
 * loading fallback on client navigations (see its layout), so this provider
 * paints the PR skeleton overlay while a navigation to another PR is in
 * flight. One shared transition also lets the pending inbox row light up no
 * matter which entry point started the navigation.
 */
export function PrNavigationProvider(t0) {
  const $ = _c(11);
  const {
    children
  } = t0;
  const router = useRouter();
  const [isPending, startTransition] = React.useTransition();
  const [target, setTarget] = React.useState(null);
  let t1;
  if ($[0] !== router) {
    t1 = (href, t2) => {
      const {
        source,
        entrance: t3
      } = t2;
      const entrance = t3 === undefined ? "in-window" : t3;
      const opensAnother = opensAnotherPullRequest(href, window.location.pathname);
      if (opensAnother) {
        recordPrNavigationStart(href, source, entrance);
      }
      if (opensAnother && !useZenModeStore.getState().isZenMode) {
        usePrV2ViewPreferencesStore.getState().setRightSidebarOpen(true);
      }
      setTarget(opensAnother ? {
        href,
        entrance
      } : null);
      startTransition(() => router.push(href));
    };
    $[0] = router;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const navigate = t1;
  const pendingTarget = isPending ? target : null;
  const t2 = pendingTarget?.href ?? null;
  let t3;
  if ($[2] !== navigate || $[3] !== t2) {
    t3 = {
      pendingHref: t2,
      navigate
    };
    $[2] = navigate;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const navigation = t3;
  let t4;
  if ($[5] !== pendingTarget) {
    t4 = pendingTarget === null ? null : <PrV2NavigationLoadingOverlay entrance={pendingTarget.entrance} />;
    $[5] = pendingTarget;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== children || $[8] !== navigation || $[9] !== t4) {
    t5 = <PrNavigationContext value={navigation}>{children}{t4}</PrNavigationContext>;
    $[7] = children;
    $[8] = navigation;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}
export function usePrNavigation(): PrNavigation {
  const context = React.use(PrNavigationContext);
  if (context === null) {
    throw new Error('usePrNavigation requires a PrNavigationProvider ancestor');
  }
  return context;
}